import { TriageDeskSummary, type useTriageDesk } from './TriageDeskSummary';

import { hotOverdueCopy } from './triageHelpers';
import { TABS, VerifiedFilter, TierFilter, LineFilter, BLANK_FILTERS, SOURCE_OPTIONS, presetRange } from './triageDeskShared';
import { useTriageController } from './useTriageController';
export function TriageQueueControls({ ctx, summary }: { ctx: ReturnType<typeof useTriageController>; summary: ReturnType<typeof useTriageDesk> }) {
const { hotOverdueError, hotOverdueLoading, tab, setTab, search, setSearch, total, loading, filters, setFilters, sortKey, setSortKey, filtersOpen, setFiltersOpen, lineFilter, setLineFilter, setAddLeadOpen, hotOverdue, hotSlaHours, setHotOverdueRefresh, shownTotal } = ctx;
return (<>
      <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Lead Triage Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Assign incoming leads to the right member. {shownTotal > 0 && (
              <span className="font-medium text-gray-700">{shownTotal} on this tab.</span>
            )}
          </p>
        </div>
        {/* CVG4-TRIAGE-1 (L117): Deepika's inlet for phone-in / walk-in /
            reference leads that never touched WhatsApp. */}
        <button
          onClick={() => setAddLeadOpen(true)}
          className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 inline-flex items-center gap-1.5"
        >
          <span aria-hidden className="text-base leading-none">+</span> New lead
        </button>
      </header>
<TriageDeskSummary resource={summary} section="kpis" onSelect={ctx.setSearch} />

      {/* Tabs — L142: the hot-overdue strip is now the 4th tab with a LIVE
          count badge (always visible, red when non-zero). */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.key === 'hot_overdue' ? (
              <>
                <span aria-hidden>🔥</span> Overdue HOT
                <span
                  className={`ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs rounded ${
                    hotOverdue.length > 0 ? 'bg-red-100 text-red-700 font-semibold' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {hotOverdueLoading ? 'Loading' : hotOverdueError ? 'Error' : hotOverdue.length}
                </span>
              </>
            ) : (
              <>
                {t.label}
                {tab === t.key && total > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                    {total}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
      </div>

      {/* L142 — Overdue-HOT tab context line. Copy follows the tenant
          hotSlaHours knob (served by the endpoint) — never hardcoded. */}
      {tab === 'hot_overdue' && hotOverdueError && <div role="alert">{hotOverdueError} <button onClick={() => setHotOverdueRefresh(n => n + 1)}>Retry overdue leads</button></div>}
      {tab === 'hot_overdue' && !hotOverdueError && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-red-800">
            Unassigned HOT — overdue ({hotOverdueLoading ? 'Loading' : hotOverdueError ? 'Error' : hotOverdue.length})
          </span>
          <span className="text-xs text-red-600">{hotOverdueCopy(hotSlaHours)}</span>
        </div>
      )}

      {/* Search + Filter bar (hidden on the Overdue-HOT tab — it renders the
          server-decided breach feed, not the filtered dashboard query) */}
      {tab !== 'hot_overdue' && (
      <div className="mb-3 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-72 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Date preset */}
        <select
          value={
            filters.dateFrom === '' && filters.dateTo === '' ? 'all' :
            (() => {
              for (const p of ['today','7d','30d','mtd']) {
                const [f,t] = presetRange(p);
                if (filters.dateFrom === f && filters.dateTo === t) return p;
              }
              return 'custom';
            })()
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'all')         setFilters({ ...filters, dateFrom: '', dateTo: '' });
            else if (v === 'custom') { /* keep current */ }
            else {
              const [from, to] = presetRange(v);
              setFilters({ ...filters, dateFrom: from, dateTo: to });
            }
          }}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
          title="Date range"
        >
          <option value="all">All time</option>
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="mtd">Month to date</option>
          <option value="custom">Custom…</option>
        </select>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5"
          title="From date"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5"
          title="To date"
        />

        {/* Source chips */}
        <div className="flex items-center gap-1">
          {SOURCE_OPTIONS.map((s) => {
            const active = filters.sources.includes(s);
            return (
              <button
                key={s}
                onClick={() => setFilters({
                  ...filters,
                  sources: active ? filters.sources.filter((x) => x !== s) : [...filters.sources, s],
                })}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  active
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
              >{s}</button>
            );
          })}
        </div>

        {/* Verified status */}
        <select
          value={filters.verified}
          onChange={(e) => setFilters({ ...filters, verified: e.target.value as VerifiedFilter })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
          title="Verified status"
        >
          <option value="any">Verified: any</option>
          <option value="verified">Verified only</option>
          <option value="unverified">Unverified only</option>
        </select>

        {/* Avika — lead tier filter (Hot / Warm / Cold / Blank) */}
        <select
          value={filters.tier}
          onChange={(e) => setFilters({ ...filters, tier: e.target.value as TierFilter })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
          title="Lead tier"
        >
          <option value="any">Tier: any</option>
          <option value="HOT">Hot</option>
          <option value="WARM">Warm</option>
          <option value="COLD">Cold</option>
          <option value="BLANK">Blank (—)</option>
        </select>

        {/* Avika — WhatsApp line filter */}
        <div className="flex items-center gap-1" title="WhatsApp number / line">
          <span className="text-[11px] text-gray-500">Line:</span>
          {(['all', 'line1', 'line2'] as LineFilter[]).map((l) => (
            <button
              key={l}
              onClick={() => setLineFilter(l)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                lineFilter === l
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            >
              {l === 'all' ? 'All' : l === 'line1' ? 'Line 1' : 'Line 2'}
            </button>
          ))}
        </div>

        {/* LBR #12 — Latest-reply sort toggle (default sort). Orders by the last
            WhatsApp/Avika reply, newest first; no-reply leads fall back to
            newest-created server-side. */}
        <button
          onClick={() => setSortKey(sortKey === 'last_reply_desc' ? 'newest' : 'last_reply_desc')}
          className={`text-xs px-2 py-1.5 rounded border transition-colors ${
            sortKey === 'last_reply_desc'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
          }`}
          title="Sort by latest reply"
        >
          Latest reply {sortKey === 'last_reply_desc' ? '↓' : '↕'}
        </button>

        {/* Avika — Score sort toggle */}
        <button
          onClick={() => setSortKey(sortKey === 'score_desc' ? 'score_asc' : 'score_desc')}
          className={`text-xs px-2 py-1.5 rounded border transition-colors ${
            sortKey === 'score_desc' || sortKey === 'score_asc'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
          }`}
          title="Sort by lead score"
        >
          Score {sortKey === 'score_desc' ? '↓' : sortKey === 'score_asc' ? '↑' : '↕'}
        </button>

        {/* More filters toggle */}
        <button
          onClick={() => setFiltersOpen((o) => !o)}
          className="text-xs px-2 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
        >
          {filtersOpen ? 'Less ▴' : 'More ▾'}
        </button>

        {/* Clear all (only when filter active) */}
        {(filters.dateFrom || filters.dateTo || filters.sources.length || filters.applicationTypes.length ||
          filters.businessSegments.length || filters.location || filters.verified !== 'any' ||
          filters.verifiedSource !== 'any' || filters.tier !== 'any' || lineFilter !== 'all') && (
          <button
            onClick={() => { setFilters(BLANK_FILTERS); setLineFilter('all'); }}
            className="text-xs text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline"
          >Clear all</button>
        )}

      </div>
      )}

      {/* Expanded filter row */}</>);
}
